import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { MARQUEE } from "@/constants";
import { ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1494403687614-8ca3e13f154f?crop=entropy&cs=srgb&fm=jpg&q=90&w=2560";
const SILK = "https://images.unsplash.com/photo-1518893063132-36e46dbe2428?crop=entropy&cs=srgb&fm=jpg&q=90&w=2000";
const COUPLE = "https://images.unsplash.com/photo-1553010254-267b14135d9b?crop=entropy&cs=srgb&fm=jpg&q=90&w=1600";
const SMOKE = "https://images.unsplash.com/photo-1637827205764-a7ad079b987b?crop=entropy&cs=srgb&fm=jpg&q=90&w=1600";

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 1, delay: 0.3 + i * 0.15, ease: [0.22, 1, 0.36, 1] } }),
};

function Line({ children, i }) {
  return (
    <span className="reveal-mask">
      <motion.span variants={lineReveal} custom={i} initial="hidden" animate="show" className="block">
        {children}
      </motion.span>
    </span>
  );
}

const CHAPTERS = [
  { n: "01", t: "Desire, without apology", img: COUPLE, vMark: true,
    d: "Velluto is built for adults who know exactly what they want. Browse and filter by orientation, relationship intent, and the fantasies that move you — no judgement, no noise." },
  { n: "02", t: "Discretion as a default", img: SILK,
    d: "Every profile ships with Discreet Mode — faces blurred until trust is earned. Reveal on your terms, to the people who deserve it." },
  { n: "03", t: "Consent is the whole point", img: SMOKE,
    d: "A consent checklist, easy blocking and reporting, and boundaries stated up front. Play begins where safety is certain." },
];

export default function Landing() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <div className="pt-16">
      {/* HERO */}
      <section ref={ref} className="relative min-h-[92vh] flex items-end overflow-hidden">
        <motion.img
          style={{ y, scale }}
          src={HERO}
          alt=""
          className="absolute inset-0 w-full h-[120%] object-cover opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-10 pb-20 w-full">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 1 }}
            className="text-xs uppercase tracking-[0.4em] text-gold mb-8"
          >
            A private dating society — 18+
          </motion.p>
          <h1 className="font-display font-light text-[13vw] md:text-[8.5vw] leading-[0.95] tracking-tight">
            <Line i={0}>Where the</Line>
            <Line i={1}><span className="italic text-gold">taboo</span> becomes</Line>
            <Line i={2}>a conversation.</Line>
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.7, duration: 1 }}
            className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-6"
          >
            <p className="max-w-md text-text-muted leading-relaxed">
              Fetish-friendly matching for casual encounters, discreet arrangements, role-play,
              and slow-burning long-term connections. Consenting adults only.
            </p>
            <Link
              to="/auth?mode=register"
              data-testid="hero-cta"
              className="group flex items-center gap-3 px-8 py-4 bg-wine hover:bg-wine-hover transition-colors rounded-full text-sm uppercase tracking-[0.2em] whitespace-nowrap"
            >
              Request access
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-10 border-y border-white/5">
        <Marquee speed={40} gradient gradientColor="#0A0A0A" gradientWidth={120}>
          {MARQUEE.map((w, i) => (
            <span key={i} className="font-display italic text-5xl md:text-7xl text-white/10 mx-10">
              {w}
            </span>
          ))}
        </Marquee>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="max-w-7xl mx-auto px-6 py-32 space-y-40">
        {CHAPTERS.map((c, i) => (
          <div key={c.n} className={`grid md:grid-cols-2 gap-12 md:gap-20 items-center ${i % 2 ? "md:[direction:rtl]" : ""}`}>
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }} whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }} transition={{ duration: 1 }}
              className="relative aspect-[4/5] overflow-hidden rounded-sm [direction:ltr]"
            >
              <img src={c.img} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
              {c.vMark && (
                <motion.span
                  data-testid="chapter-v-mark"
                  initial={{ opacity: 0, scale: 0.85 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                  className="v-mark absolute inset-0 flex items-center justify-center text-[13rem] md:text-[16rem] leading-none pointer-events-none select-none"
                >
                  V
                </motion.span>
              )}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.9, delay: 0.1 }}
              className="[direction:ltr]"
            >
              <span className="font-display text-7xl text-gold/40 leading-none">{c.n}</span>
              <h2 className="font-display text-5xl md:text-6xl font-light leading-none mt-4 mb-8">{c.t}</h2>
              <p className="text-text-muted text-lg leading-relaxed max-w-md">{c.d}</p>
            </motion.div>
          </div>
        ))}
      </section>

      {/* CTA */}
      <section className="relative py-40 overflow-hidden">
        <img src={SILK} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-ink/70" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-display text-6xl md:text-7xl font-light leading-none mb-8">
            Your fantasies<br /><span className="italic text-gold">are welcome here.</span>
          </h2>
          <p className="text-text-muted mb-12 max-w-lg mx-auto">
            Join a community that treats desire with sophistication and discretion.
            Membership is free to start.
          </p>
          <Link
            to="/auth?mode=register"
            data-testid="cta-join"
            className="inline-flex items-center gap-3 px-12 py-5 bg-wine hover:bg-wine-hover transition-colors rounded-full text-sm uppercase tracking-[0.25em]"
          >
            Create your profile <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/5 py-12 text-center">
        <p className="brand-script text-5xl">Velluto</p>
        <p className="text-xs text-text-muted mt-3 tracking-wide">
          Adults only (18+). A space for consenting connections. © 2026 Velluto Society.
        </p>
      </footer>
    </div>
  );
}
